import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FieldRequiredMark } from '../Form/FieldRequiredMark';

/*
 * The backend leaves out modules holding nothing that can be generated from.
 * Said here rather than by each caller so the two generators cannot disagree
 * about it, and so a course's missing modules are never just unexplained.
 */
const HIDDEN_MODULES_NOTE = 'Published modules with no available content are hidden in this list.';

/**
 * Renders the "Resources" section of a generator form: one expandable row per
 * module, then a select-all control underneath them.
 *
 * A module is chosen as a whole. What it contains is listed underneath so the
 * learner can see what they are asking about, but those rows are not selectable
 * — generation is requested per module.
 *
 * Props:
 *   id           (string)   - ID prefix for generated element IDs.
 *   modules      (array)    - [{ id, name, resources?: [{ id, title, description }] }]
 *   selected     (array)    - Currently selected module IDs.
 *   onChange     (function) - Called with the next array of selected module IDs.
 *   label        (string)   - Section heading. Defaults to "Resources".
 *   helperText   (string)   - Sentence under the heading, before the note about
 *                              which modules are left out.
 *   required     (bool)     - Marks the heading with an asterisk.
 */
function ResourceSelector({
  id,
  modules = [],
  selected = [],
  onChange,
  label = 'Resources',
  helperText = 'Select resources from which the deck will be generated.',
  required,
}) {
  const selectedIds = new Set(selected);
  const allIds = modules.map((module) => module.id);

  const areAllSelected = allIds.length > 0 && allIds.every((value) => selectedIds.has(value));
  const areSomeSelected = !areAllSelected && allIds.some((value) => selectedIds.has(value));

  const setSelection = (ids, isSelected) => {
    const next = new Set(selectedIds);
    ids.forEach((value) => (isSelected ? next.add(value) : next.delete(value)));
    // Preserve module order rather than click order so the payload stays stable.
    onChange(allIds.filter((value) => next.has(value)));
  };

  const handleToggleAll = () => setSelection(allIds, !areAllSelected);

  const handleToggleModule = (moduleId) => setSelection([moduleId], !selectedIds.has(moduleId));

  return (
    <Box className="mss__resourceSelector" id={`${id}__box_resourceSelector`}>
      <Typography className="sectionLabel sectionLabel--accent">
        {/* Part of the heading, in the heading's own colour, as the design has it. */}
        {label} {required && <FieldRequiredMark label={label} />}
      </Typography>
      <Typography className="sectionHelper">
        {helperText} {HIDDEN_MODULES_NOTE}
      </Typography>

      <Box className="moduleList" id={`${id}__box_moduleList`}>
        {modules.map((module) => {
          const hasResources = !!module.resources?.length;

          return (
            <Box className="moduleRow" key={module.id} id={`${id}__box_module_${module.id}`}>
              <Checkbox
                id={`${id}__checkbox_module_${module.id}`}
                checked={selectedIds.has(module.id)}
                onChange={() => handleToggleModule(module.id)}
                size="small"
                slotProps={{ input: { 'aria-label': module.name } }}
              />

              {hasResources ? (
                <Accordion className="moduleAccordion" disableGutters elevation={0}>
                  <AccordionSummary
                    id={`${id}__accordionSummary_module_${module.id}`}
                    aria-controls={`${id}__accordionDetails_module_${module.id}`}
                    expandIcon={
                      <FontAwesomeIcon icon={faChevronDown} className="moduleExpandIcon" />
                    }
                  >
                    <Typography className="moduleName">{module.name}</Typography>
                  </AccordionSummary>

                  {/* Listed so the learner can see what the module covers. */}
                  <AccordionDetails id={`${id}__accordionDetails_module_${module.id}`}>
                    {module.resources.map((resource) => (
                      <Box
                        className="resourceRow"
                        key={resource.id}
                        id={`${id}__box_resource_${resource.id}`}
                      >
                        <Box className="resourceText">
                          <Typography className="resourceTitle">{resource.title}</Typography>
                          {resource.description && (
                            <Typography className="resourceDescription">
                              {resource.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </AccordionDetails>
                </Accordion>
              ) : (
                /*
                 * A module Canvas lists no contents for still gets the same box,
                 * so the list reads as one column — it just has nothing to
                 * expand. The name is bound to the checkbox so it stays a click
                 * target, the way a FormControlLabel would.
                 */
                <Box className="moduleAccordion moduleAccordion--empty">
                  <Typography
                    className="moduleName"
                    component="label"
                    htmlFor={`${id}__checkbox_module_${module.id}`}
                  >
                    {module.name}
                  </Typography>
                </Box>
              )}
            </Box>
          );
        })}

        {/* The last row of the list it applies to, drawn like the modules above it. */}
        <Box className="moduleRow" id={`${id}__box_selectAll`}>
          <Checkbox
            id={`${id}__checkbox_selectAll`}
            checked={areAllSelected}
            indeterminate={areSomeSelected}
            onChange={handleToggleAll}
            disabled={allIds.length === 0}
            size="small"
            slotProps={{ input: { 'aria-label': 'All Modules' } }}
          />

          <Box className="moduleAccordion moduleAccordion--empty">
            <Typography
              className="moduleName"
              component="label"
              htmlFor={`${id}__checkbox_selectAll`}
            >
              All Modules
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default ResourceSelector;
